import { Plus, TrendingUp } from 'lucide-react'
import { useId, useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { useIsAdmin } from '@/lib/auth'
import { parseAmountInput, toDecimal, toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { useAddRatePeriod, useRateIndexationApply, useRateIndexationPreview } from '@/lib/clients-api'
import { usePersons } from '@/lib/settings-api'
import type { ClientDetail } from '@/lib/clients-types'
import { cn } from '@/lib/utils'
import { MONTH_NAMES, PERSON_NONE } from './shared'

function PersonSelect({
  value,
  onChange,
  emptyLabel,
  className,
  ariaLabelledby,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  emptyLabel: string
  className?: string
  ariaLabelledby?: string
  disabled?: boolean
}) {
  const personsQuery = usePersons()
  const persons = personsQuery.data ?? []
  return (
    <Select
      value={value === '' ? PERSON_NONE : value}
      onValueChange={(v) => onChange(v === PERSON_NONE ? '' : v)}
      options={[{ value: PERSON_NONE, label: emptyLabel }, ...persons.map((p) => ({ value: p.displayName, label: p.displayName }))]}
      ariaLabelledby={ariaLabelledby}
      className={cn('w-full', className)}
      disabled={disabled}
    />
  )
}

export function RatesCard({ client }: { client: ClientDetail }) {
  const isAdmin = useIsAdmin()
  const [open, setOpen] = useState(false)
  const [indexOpen, setIndexOpen] = useState(false)
  return (
    <Card>
      <CardHeader
        title="Verkooptarieven"
        subtitle={
          client.indexationMonth
            ? `Wat deze klant per uur betaalt · indexatie jaarlijks per ${MONTH_NAMES[client.indexationMonth - 1]}`
            : 'Wat deze klant per uur betaalt · nieuwe periode sluit de vorige automatisch af'
        }
        action={
          isAdmin ? (
            <div className="flex items-center gap-1">
              {client.indexationMonth ? (
                <button type="button" className="btn-ghost h-8 px-2 text-sm" onClick={() => setIndexOpen(true)}>
                  <TrendingUp className="h-4 w-4" /> Indexeer
                </button>
              ) : null}
              <button type="button" className="btn-ghost h-8 px-2 text-sm" onClick={() => setOpen(true)}>
                <Plus className="h-4 w-4" /> Periode
              </button>
            </div>
          ) : null
        }
      />
      {client.indexationPending && isAdmin ? (
        <button
          type="button"
          onClick={() => setIndexOpen(true)}
          className="mt-3 flex w-full items-center justify-between rounded-lg border border-status-overdue/30 bg-status-overdue/6 px-3 py-2 text-left"
        >
          <span className="text-sm font-medium text-status-overdue">Indexatie van dit jaar staat nog open</span>
          <span className="text-xs text-status-overdue">Indexeer nu →</span>
        </button>
      ) : null}
      <div className="mt-4 space-y-2">
        {client.ratePeriods.map((rate) => (
          <div key={rate.id} className={cn('flex items-center justify-between rounded-lg border border-hairline px-3 py-2', rate.validTo && 'opacity-60')}>
            <div>
              <span className="text-sm font-medium text-ink">
                {rate.projectName ?? (rate.personName ? `Uren van ${rate.personName}` : 'Standaardtarief')}
              </span>
              {rate.projectName && rate.personName ? <span className="ml-1.5 text-xs text-ink-faint">uren van {rate.personName}</span> : null}
              <span className="ml-2 text-xs text-ink-faint">
                {rate.validFrom} {rate.validTo ? `t/m ${rate.validTo}` : '· actueel'}
              </span>
            </div>
            <span className="money text-sm font-medium text-ink">{formatEuros(toNumber(rate.hourlyRate))}/u</span>
          </div>
        ))}
      </div>
      {open ? <AddRateModal client={client} onClose={() => setOpen(false)} /> : null}
      {indexOpen ? <IndexRatesModal client={client} onClose={() => setIndexOpen(false)} /> : null}
    </Card>
  )
}

function IndexRatesModal({ client, onClose }: { client: ClientDetail; onClose: () => void }) {
  const preview = useRateIndexationPreview(client.id)
  const apply = useRateIndexationApply(client.id)
  const suggestion = client.suggestedPercent
  const [percent, setPercent] = useState(suggestion ? String(toNumber(suggestion.percent)).replace('.', ',') : '')

  const percentDecimal = percent.trim() ? toDecimal(toNumber(percent.replace(',', '.'))) : null

  function runPreview() {
    if (percentDecimal) preview.mutate({ percent: percentDecimal })
  }

  function runApply() {
    if (preview.data) {
      apply.mutate({ percent: preview.data.percent, validFrom: preview.data.validFrom }, { onSuccess: onClose })
    }
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Verkooptarieven indexeren"
      description="Alle lopende tariefperioden krijgen een nieuwe, verhoogde periode."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
          {preview.data ? (
            <button type="button" className="btn-primary" onClick={runApply} disabled={apply.isPending}>
              {apply.isPending ? 'Toepassen…' : 'Indexatie toepassen'}
            </button>
          ) : (
            <button type="button" className="btn-primary" onClick={runPreview} disabled={!percentDecimal || preview.isPending}>
              Preview
            </button>
          )}
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Percentage</span>
          <input
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            inputMode="decimal"
            className="field num"
            placeholder="3,10"
            autoFocus
          />
          {suggestion ? (
            <span className="mt-1 block text-xs text-ink-faint">
              {suggestion.source === 'this_year'
                ? `Voorstel op basis van een eerdere indexatie in ${suggestion.sourceYear}.`
                : `Voorstel op basis van het percentage van ${suggestion.sourceYear}.`}
            </span>
          ) : null}
        </label>
        {preview.data ? (
          <div className="rounded-lg border border-accent-500/30 bg-accent-600/4 p-3">
            <p className="mb-2 text-sm font-medium text-ink">
              +{toNumber(preview.data.percent)}% per {preview.data.validFrom}
            </p>
            <div className="space-y-1.5">
              {preview.data.lines.map((line) => (
                <div key={line.rateId} className="flex items-center justify-between text-sm">
                  <span className="text-ink-soft">{line.scope}</span>
                  <span className="money text-ink-faint">
                    {formatEuros(toNumber(line.oldRate))}/u <span className="text-accent-700">→ {formatEuros(toNumber(line.newRate))}/u</span>
                  </span>
                </div>
              ))}
            </div>
            {preview.data.skipped.length > 0 ? (
              <p className="mt-2 border-t border-hairline pt-2 text-xs text-ink-faint">
                Overgeslagen (ingegaan op of na {preview.data.validFrom}): {preview.data.skipped.map((s) => s.scope).join(', ')}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  )
}

function AddRateModal({ client, onClose }: { client: ClientDetail; onClose: () => void }) {
  const personLabelId = useId()
  const addRate = useAddRatePeriod(client.id)
  const [person, setPerson] = useState('')
  const [rate, setRate] = useState('')
  const [validFrom, setValidFrom] = useState(new Date().toISOString().slice(0, 10))
  const [projectId, setProjectId] = useState('none')

  const parsedRate = parseAmountInput(rate)
  const rateInvalid = rate.trim() !== '' && (parsedRate === null || parsedRate <= 0)

  function save() {
    if (parsedRate === null || parsedRate <= 0) return
    addRate.mutate(
      {
        personName: person || null,
        hourlyRate: toDecimal(parsedRate),
        validFrom,
        projectId: projectId !== 'none' ? projectId : undefined,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Nieuwe verkooptariefperiode"
      description="Wat deze klant ons per uur betaalt. De lopende periode voor dezelfde combinatie wordt automatisch afgesloten."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
          <button type="button" className="btn-primary" onClick={save} disabled={parsedRate === null || parsedRate <= 0 || addRate.isPending}>Toevoegen</button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span id={personLabelId} className="mb-1.5 block text-sm font-medium text-ink-soft">Geldt voor</span>
          <PersonSelect
            value={person}
            onChange={(v) => {
              setPerson(v)
              if (v !== '') setProjectId('none')
            }}
            emptyLabel="Alle personen (standaardtarief)"
            ariaLabelledby={personLabelId}
            disabled={projectId !== 'none'}
          />
          <span className="mt-1 block text-xs text-ink-faint">
            Kies een persoon als deze klant voor diens uren een afwijkend bedrag betaalt. Dit staat los van het uitbetaaltarief van die persoon (Instellingen → Personen).
          </span>
          {projectId !== 'none' ? (
            <span className="mt-1 block text-xs text-ink-faint">Persoonsafwijkingen zijn klant-breed en kunnen niet aan een project gekoppeld worden.</span>
          ) : null}
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Verkooptarief per uur €</span>
          <input
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            inputMode="decimal"
            className={cn('field num text-right', rateInvalid && 'border-status-overdue')}
            placeholder="110,00"
          />
          {rateInvalid ? <span className="mt-1 block text-xs text-status-overdue">Onleesbaar bedrag, gebruik bijv. 110,00</span> : null}
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Geldig vanaf</span>
          <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="field" />
        </label>
        {client.separateInvoicePerProject && client.projects.length > 0 ? (
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Project</span>
            <Select
              value={projectId}
              onValueChange={(v) => {
                setProjectId(v)
                if (v !== 'none') setPerson('')
              }}
              options={[{ value: 'none', label: 'Alle projecten (klantbreed)' }, ...client.projects.map((p) => ({ value: p.id, label: p.name }))]}
              disabled={person !== ''}
            />
          </label>
        ) : null}
      </div>
    </Modal>
  )
}
